import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle, X, Rocket, ChevronRight, Gift } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/kit";

function RewardCard({ onClose }) {
  return (
    <Card className="mb-6 fade-in flex items-center gap-4" style={{ background: "var(--brand)", color: "#fff" }} data-testid="onboarding-reward-card">
      <Gift size={30} className="shrink-0" />
      <div className="flex-1">
        <div className="text-lg font-bold">Parabéns! Você ganhou 20% de desconto</div>
        <p className="text-sm opacity-90">Por concluir todos os primeiros passos no teste grátis. O desconto entra sozinho na 1ª mensalidade.</p>
      </div>
      <Link to="/billing" className="btn" style={{ background: "#fff", color: "var(--brand)" }} data-testid="onboarding-reward-billing">Ver assinatura</Link>
      <button className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ color: "#fff", background: "rgba(255,255,255,0.18)" }} onClick={onClose} title="Fechar" data-testid="onboarding-reward-close"><X size={15} /></button>
    </Card>
  );
}

export function OnboardingChecklist() {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["onboarding"], queryFn: () => api("/onboarding"), enabled: isAdmin });
  const hide = useMutation({ mutationFn: (reward) => api("/onboarding", { method: "PUT", body: { hidden: true, reward } }), onSuccess: () => qc.invalidateQueries({ queryKey: ["onboarding"] }) });
  if (!isAdmin || !data) return null;
  if (data.reward?.applied && !data.reward_dismissed && (data.done === data.total || data.hidden)) return <RewardCard onClose={() => hide.mutate(true)} />;
  if (data.hidden || data.done === data.total) return null;
  const pct = Math.round((100 * data.done) / data.total);
  const next = data.items.find((i) => !i.done);
  return (
    <Card className="mb-6 fade-in" data-testid="onboarding-checklist">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--brand-tint)", color: "var(--brand)" }}><Rocket size={19} /></span>
        <div className="flex-1 min-w-0">
          <div className="h3">Primeiros passos</div>
          <p className="caption tnum" data-testid="onboarding-progress-text">{data.done} de {data.total} concluídos · os itens se marcam sozinhos conforme você usa o sistema</p>
          {data.reward?.available && !data.reward.applied && <p className="text-sm font-semibold mt-1 flex items-center gap-1.5" style={{ color: "var(--brand)" }} data-testid="onboarding-reward-hint"><Gift size={14} /> Conclua tudo durante o teste grátis e ganhe {data.reward.label}</p>}
        </div>
        <button className="icon-btn" onClick={() => window.confirm("Esconder a lista de primeiros passos?") && hide.mutate(false)} title="Esconder" data-testid="onboarding-hide-button"><X size={15} /></button>
      </div>
      <div className="h-2 rounded-full mt-4 overflow-hidden" style={{ background: "var(--surface-alt)" }}><div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, background: "var(--brand)" }} data-testid="onboarding-progress-bar" /></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-4">
        {data.items.map((i) => (
          <Link key={i.id} to={i.to} className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 transition-colors" style={{ background: i.id === next?.id ? "var(--brand-tint)" : "transparent" }} data-testid={`onboarding-item-${i.id}`}>
            {i.done ? <CheckCircle2 size={18} style={{ color: "var(--success)" }} data-testid={`onboarding-done-${i.id}`} /> : <Circle size={18} style={{ color: "var(--muted)" }} />}
            <span className={`text-sm flex-1 ${i.done ? "line-through opacity-60" : "font-medium"}`}>{i.label}</span>
            {!i.done && <ChevronRight size={15} style={{ color: "var(--muted)" }} />}
          </Link>
        ))}
      </div>
    </Card>
  );
}
